import { Font, pdf, StyleSheet } from '@react-pdf/renderer';
import { useCallback, useMemo, useState } from 'react';
import { GeneratedCreditPlanProps } from '../Panels/GeneratedCreditPlanSidePanel';
import { formatDate } from '@/utils/helpers';
import { CreditPlan } from '@/@types/client-api';
import { Pdf } from '@/@types/content';
import type { IconMessageBlock, RichTextBlock as RichTextBlockProps } from '@/@types/content';
import { expandContent } from '@/lib/helpers/expandChildren';
import useToast from '@/hooks/useToast';
import { datadogRum } from '@/lib/DatadogService';
import CreditPlanPdf from './CreditPlanPdf';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { selectPageLinkPaths } from '@/redux/selectors/pageSelectors';
import { getTrackerUrl } from '../util/CreditPlanHelpers';

export type CreditPlanPdfHookProps = Pick<
  GeneratedCreditPlanProps,
  'aiPoweredText' | 'complianceEndDate' | 'jurisdictionName' | 'jurisdictionId'
> & {
  heading: string;
  subheading?: string;
  disclaimer?: RichTextBlockProps;
  content?: Pdf;
  betaIcon?: boolean;
  plan: CreditPlan;
};

const useCreditPlanPdf = ({
  aiPoweredText,
  complianceEndDate,
  jurisdictionName,
  disclaimer,
  content,
  betaIcon,
  plan,
  jurisdictionId
}: CreditPlanPdfHookProps) => {
  const { showFailMessage } = useToast();
  const authState = useSelector((state: RootState) => state.auth);
  const { CreditTrackerPage } = useSelector(selectPageLinkPaths);
  const [isPdfViewOpen, setIsPdfViewOpen] = useState<boolean>(false);

  Font.registerHyphenationCallback(word => [word]);
  const fontBase = typeof window !== 'undefined' ? `${window.location.origin}/fonts` : '/fonts';

  Font.register({
    family: 'Helvetica Neue',
    fonts: [
      {
        src: `${fontBase}/HelveticaNeue/HelveticaNeueLTW04-55Roman.woff`,
        fontWeight: 400,
        fontStyle: 'normal'
      },
      {
        src: `${fontBase}/HelveticaNeue/HelveticaNeueLTW04-56Italic.woff`,
        fontWeight: 400,
        fontStyle: 'italic'
      },
      {
        src: `${fontBase}/HelveticaNeue/HelveticaNeueLTW04-65Medium.woff`,
        fontWeight: 500,
        fontStyle: 'normal'
      },
      {
        src: `${fontBase}/HelveticaNeue/HelveticaNeueLTW04-66MdIt.woff`,
        fontWeight: 500,
        fontStyle: 'italic'
      },
      {
        src: `${fontBase}/HelveticaNeue/HelveticaNeueLTW04-75Bold.woff`,
        fontWeight: 700,
        fontStyle: 'normal'
      }
    ]
  });

  const section = useMemo(
    () => ({
      padding: 38
    }),
    []
  );

  const styles = useMemo(
    () =>
      StyleSheet.create({
        section: {
          ...section
        },
        page: {
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#FFF',
          color: '#888',
          fontFamily: 'Helvetica Neue',
          fontStyle: 'normal',
          paddingVertical: 10
        },
        documentTitle: {
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          maxHeight: 20,
          marginBottom: 28
        },
        topSection: {
          ...section,
          backgroundColor: '#F5F5F5',
          paddingTop: 24,
          maxHeight: 320
        },
        aiText: {
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          justifyContent: 'flex-start',
          alignItems: 'center',
          color: '#53565A',
          width: 'auto'
        },
        heading: {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start'
        },
        headingText: {
          marginTop: 2,
          width: '100%',
          textAlign: 'left'
        },
        jurisdictionName: {
          fontFamily: 'Helvetica-Bold',
          fontWeight: 500,
          fontSize: 48,
          color: '#000',
          marginBottom: 24
        },
        liveProgramCard: {
          backgroundColor: '#2C2C4A',
          borderRadius: 8,
          padding: 24,
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          fontSize: 14,
          color: '#FFF',
          fontWeight: 500,
          minHeight: 240
        },
        liveProgramTextContainer: {
          flex: 1,
          paddingRight: 12,
          maxWidth: '75%',
          width: 'auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        },
        liveProgramRegisterButton: {
          backgroundColor: '#FFF',
          color: '#000',
          fontSize: 14,
          fontWeight: 600,
          paddingVertical: 10,
          paddingHorizontal: 24,
          letterSpacing: -0.28,
          borderRadius: 4,
          alignSelf: 'flex-start',
          textDecoration: 'none'
        },
        liveProgramDate: {
          backgroundColor: '#40406E',
          borderRadius: 6,
          paddingVertical: 10,
          paddingHorizontal: 14,
          alignItems: 'center',
          minWidth: 52
        },
        disclaimerElement: {
          ...section,
          fontSize: 16,
          fontWeight: 400,
          color: '#53565A'
        }
      }),
    [section]
  );

  const fileName = useMemo(() => {
    const date = formatDate(new Date(), 'M-d-yyyy');
    return `PLI_${jurisdictionName?.replaceAll(' ', '-')}_CreditPlan_${date}.pdf`;
  }, [jurisdictionName]);

  const dateDisplay = useMemo(() => {
    return formatDate(new Date(), 'MMMM d, yyyy');
  }, []);

  const pmMessage = useMemo(() => {
    if (authState?.isPm && content?.pmMessage?.length) {
      return expandContent(content.pmMessage)?.filter(
        a => !!(a as IconMessageBlock)
      )?.[0] as IconMessageBlock;
    }
  }, [authState?.isPm, content?.pmMessage]);

  const trackerLink = useMemo(
    () => getTrackerUrl(CreditTrackerPage, jurisdictionId),
    [CreditTrackerPage, jurisdictionId]
  );

  const showDownloadFailureToast = useCallback(() => {
    showFailMessage('Failed to download credit plan');
  }, [showFailMessage]);

  const PdfDocument = useMemo(
    () => (
      <CreditPlanPdf
        {...{
          dateDisplay,
          fileName,
          styles,
          aiPoweredText,
          complianceEndDate,
          content,
          disclaimer,
          jurisdictionName,
          betaIcon,
          pmMessage,
          plan,
          jurisdictionId,
          trackerLink
        }}
      />
    ),
    [
      dateDisplay,
      fileName,
      styles,
      aiPoweredText,
      complianceEndDate,
      content,
      disclaimer,
      jurisdictionName,
      betaIcon,
      pmMessage,
      plan,
      jurisdictionId,
      trackerLink
    ]
  );

  const triggerDownload = useCallback(async () => {
    try {
      const blob = await pdf(PdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (ex) {
      datadogRum.addError(ex as Error);
      showDownloadFailureToast();
    }
  }, [fileName, PdfDocument, showDownloadFailureToast]);

  return {
    triggerDownload,
    showDownloadFailureToast,
    setIsPdfViewOpen,
    isPdfViewOpen,
    PdfDocument,
    styles,
    dateDisplay,
    pmMessage
  };
};

export default useCreditPlanPdf;
